import React, { useEffect, useState } from "react";
import api from "../../utils/api";
import { Link, useParams } from "react-router-dom";
import { FieldDefinition } from "../trackers/types";
import { renderFieldValue } from "../trackers/TrackerDetail";
import {
	Alert,
	Box,
	Container,
	Grid,
	Paper,
	Typography,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	IconButton,
	Button,
} from "@mui/material";
import { OpenInNew, Assignment } from "@mui/icons-material";
import FullPageLoader from "../../components/FullPageLoader";
import ImageLink from "../../components/ImageLink";
import { ContainerEdit } from "./ContainerEdit";
import { format } from "date-fns";
import useRouteGuard from "../../hooks/useRouteGuard";

const containerFields: FieldDefinition[] = [
	{ field: "containerNumber", label: "Container Number" },
	{
		field: "seenAt",
		label: "Seen at",
		parser: (v) => format(new Date(v), "MMM dd - HH:mm"),
	},
	{ field: "location", label: "Location" },
	{ field: "trailerPlate", label: "Trailer Plate" },
	{ field: "truckPlate", label: "Truck Plate" },
	{ field: "intelligence", label: "Intelligence" },
];

interface PlateLookupResult {
	plate: string;
	smviu: {
		vehicleId: number;
		stolenStatus: string;
	} | null;
	rdw: {
		status: "found" | "not-found";
	};
}

const getSmviuStatus = (result: PlateLookupResult) => {
	if (result.smviu) {
		return result.smviu.stolenStatus === "stolen" ? "stolen" : "not-stolen";
	}
	return "not-found";
};

const getSmviuStatusColor = (status: string) => {
	switch (status) {
		case "stolen":
			return "error";
		case "not-stolen":
			return "success";
		case "not-found":
			return "default";
		default:
			return "default";
	}
};

const getRdwStatus = (result: PlateLookupResult) => {
	return result.rdw.status;
};

const getRdwStatusColor = (status: string) => {
	return status === "found" ? "info" : "default";
};

const ContainerDetail = () => {
	useRouteGuard(undefined, "canViewContainers");
	const { id } = useParams<{ id: string }>();
	const [data, setData] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState("");

	useEffect(() => {
		loadData();
	}, []);

	async function loadData() {
		setIsLoading(true);
		const resp = await api.get(`/containers/${id}/`);
		resp.error && setError("Something went wrong!");
		setData(resp.data || null);
		setIsLoading(false);
	}

	if (isLoading) {
		return <FullPageLoader />;
	}

	if (error) {
		return (
			<Container>
				<Alert variant="outlined" severity="error" sx={{ mt: 4 }}>
					{error}
				</Alert>
			</Container>
		);
	}

	if (!data) {
		return (
			<Container>
				<Alert variant="outlined" severity="info" sx={{ mt: 4 }}>
					No data available!
				</Alert>
			</Container>
		);
	}

	return (
		<Box p={4}>
			<Grid container spacing={2} alignItems="stretch">
				<Grid size={{ xs: 12, lg: 6 }}>
					<Paper sx={{ p: 2, height: "100%" }}>
						<Box display="flex" gap={2} alignItems="center" mb={2}>
							<Typography
								variant="h5"
								sx={{
									textDecoration: "underline",
									textUnderlineOffset: 6,
									flexGrow: 1,
								}}
							>
								Container Details
							</Typography>
							<Button
								variant="outlined"
								startIcon={<Assignment />}
								component={Link}
								to={`/containers/${id}/alert-report/`}
								style={{ marginBottom: 24 }}
							>
								Generate Alert Report
							</Button>
							<ContainerEdit data={data} />
						</Box>
						{containerFields.map(
							(field, i) =>
								(!field.hideIfEmpty || !!data[field.field]) && (
									<Box
										key={field.field}
										display="grid"
										gridTemplateColumns="220px 1fr"
										gap={2}
										px={2}
										py={1}
										sx={{ backgroundColor: i % 2 == 0 ? "#f5f5f5" : "white" }}
									>
										<div>{field.label}</div>
										<div>
											{renderFieldValue(data[field.field], field.parser)}
										</div>
									</Box>
								),
						)}
					</Paper>
				</Grid>
				<Grid size={{ xs: 12, lg: 6 }}>
					<Paper sx={{ p: 2, height: "100%" }}>
						<Typography
							variant="h5"
							sx={{ textDecoration: "underline", textUnderlineOffset: 6 }}
							mb={2}
						>
							Images
						</Typography>
						<Box
							display="flex"
							gap={4}
							sx={{ "& img": { maxWidth: 280 } }}
							flexWrap="wrap"
						>
							{data.photos?.map(({ file }: any) => (
								<ImageLink src={file} key={file} />
							))}
						</Box>
					</Paper>
				</Grid>
			</Grid>

			{/* New Vehicle Plates Section */}
			{data.plateLookupResults && data.plateLookupResults.length > 0 && (
				<>
					<Grid container spacing={2} sx={{ mt: 2 }}>
						<Grid size={12}>
							<Paper sx={{ p: 2 }}>
								<Typography
									variant="h5"
									sx={{ textDecoration: "underline", textUnderlineOffset: 6 }}
									mb={2}
								>
									License Plates ({data.plateLookupResults.length})
								</Typography>

								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>
												<strong>Plate</strong>
											</TableCell>
											<TableCell>
												<strong>SMVIU Status</strong>
											</TableCell>
											<TableCell>
												<strong>RDW Status</strong>
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{data.plateLookupResults.map(
											(result: PlateLookupResult) => {
												const smviuStatus = getSmviuStatus(result);
												const rdwStatus = getRdwStatus(result);

												return (
													<TableRow key={result.plate}>
														<TableCell>
															<Typography variant="body2" fontWeight="bold">
																{result.plate}
															</Typography>
														</TableCell>
														<TableCell>
															<Box display="flex" alignItems="center" gap={1}>
																<Chip
																	label={smviuStatus}
																	color={
																		getSmviuStatusColor(smviuStatus) as any
																	}
																	size="small"
																/>
																{result.smviu && (
																	<IconButton
																		size="small"
																		component={Link}
																		to={`/vehicles/${result.smviu.vehicleId}/`}
																		title="View Vehicle Details"
																	>
																		<OpenInNew fontSize="small" />
																	</IconButton>
																)}
															</Box>
														</TableCell>
														<TableCell>
															<Box display="flex" alignItems="center" gap={1}>
																<Chip
																	label={rdwStatus}
																	color={getRdwStatusColor(rdwStatus) as any}
																	size="small"
																/>
																{result.rdw.status === "found" && (
																	<IconButton
																		size="small"
																		component={Link}
																		to={`/rdw/search/${result.plate}/`}
																		title="View RDW Details"
																	>
																		<OpenInNew fontSize="small" />
																	</IconButton>
																)}
															</Box>
														</TableCell>
													</TableRow>
												);
											},
										)}
									</TableBody>
								</Table>
							</Paper>
						</Grid>
					</Grid>
				</>
			)}

			{/* Show message if no plates */}
			{(!data.plateLookupResults || data.plateLookupResults.length === 0) && (
				<Grid container spacing={2} sx={{ mt: 2 }}>
					<Grid size={12}>
						<Paper sx={{ p: 2 }}>
							<Typography variant="h6" color="text.secondary">
								No license plates found in this container
							</Typography>
						</Paper>
					</Grid>
				</Grid>
			)}
		</Box>
	);
};

export default ContainerDetail;
